// Cycle store — cloud-aware. Syncs to mystack_cycle_starts and
// mystack_custom_cycles when the user is signed in (and not in local-only mode).

import { supabase, isCloudMode, currentUserSync } from './supabase';

export type CyclePhase = {
  on_phase: boolean;
  day_in_period: number;
  days_remaining: number;
  period_length: number;
  label: 'ON' | 'OFF';
};

export type CustomCycle = {
  id: string;
  label: string;
  on_days: number;
  off_days: number;
  started_at: string; // ISO
  notes: string | null;
};

const K_STARTED    = 'mystack:cycle:started:v1';
const K_CUSTOM     = 'mystack:cycle:custom:v1';
const K_DISCLAIMER = 'mystack:cycle:disclaimer:v1';

function safeParse<T>(s: string | null, fallback: T): T {
  if (!s) return fallback;
  try { return JSON.parse(s) as T; } catch { return fallback; }
}

import { uuidV4 } from './analytics';

/* ---------- Pure helpers (unchanged from prior version) ---------- */

export function parseCyclePattern(pattern: string | null): [number, number] | null {
  if (!pattern) return null;
  const m = pattern.match(/(\d+)\s*(day|week|month)s?\s+on[,\s]+(\d+)\s*(day|week|month)s?\s+off/i);
  if (!m) return null;
  const toDays = (v: number, u: string) => {
    const lower = u.toLowerCase();
    if (lower === 'day') return v;
    if (lower === 'week') return v * 7;
    if (lower === 'month') return v * 30;
    return v;
  };
  return [toDays(+m[1], m[2]), toDays(+m[3], m[4])];
}

export function phaseFor(started: Date, onDays: number, offDays: number): CyclePhase {
  const period = onDays + offDays;
  if (period <= 0) return { on_phase: true, day_in_period: 0, days_remaining: 0, period_length: 0, label: 'ON' };
  const days = Math.floor((Date.now() - started.getTime()) / 86400000);
  const dip = ((days % period) + period) % period;
  if (dip < onDays) {
    return { on_phase: true, day_in_period: dip, days_remaining: onDays - dip, period_length: period, label: 'ON' };
  }
  return { on_phase: false, day_in_period: dip, days_remaining: period - dip, period_length: period, label: 'OFF' };
}

/* ---------- Local cache ---------- */

function readStartsLocal(): Record<string, Date> {
  const raw = safeParse<Record<string, string>>(localStorage.getItem(K_STARTED), {});
  const out: Record<string, Date> = {};
  for (const [k, v] of Object.entries(raw)) { out[k] = new Date(v); }
  return out;
}
function writeStartsLocal(all: Record<string, Date>): void {
  const serial: Record<string, string> = {};
  for (const [k, v] of Object.entries(all)) serial[k] = v.toISOString();
  localStorage.setItem(K_STARTED, JSON.stringify(serial));
}
function readCustomLocal(): CustomCycle[] {
  return safeParse<CustomCycle[]>(localStorage.getItem(K_CUSTOM), []);
}
function writeCustomLocal(all: CustomCycle[]): void {
  localStorage.setItem(K_CUSTOM, JSON.stringify(all));
}

/* ---------- Cloud reads/writes ---------- */

async function fetchStartsCloud(): Promise<Record<string, Date> | null> {
  const u = currentUserSync();
  if (!u) return null;
  const { data, error } = await supabase
    .from('mystack_cycle_starts')
    .select('compound_id, started_at')
    .eq('owner_id', u.id);
  if (error || !data) return null;
  const out: Record<string, Date> = {};
  for (const r of data) {
    out[(r as any).compound_id] = new Date((r as any).started_at);
  }
  return out;
}

async function fetchCustomCloud(): Promise<CustomCycle[] | null> {
  const u = currentUserSync();
  if (!u) return null;
  const { data, error } = await supabase
    .from('mystack_custom_cycles')
    .select('id, label, on_days, off_days, started_at, notes')
    .eq('owner_id', u.id);
  if (error || !data) return null;
  return data.map((r) => ({
    id: (r as any).id,
    label: (r as any).label,
    on_days: (r as any).on_days,
    off_days: (r as any).off_days,
    started_at: (r as any).started_at,
    notes: (r as any).notes ?? null,
  }));
}

/* ---------- Public API (signatures match prior version) ---------- */

export function loadStarts(): Record<string, Date> { return readStartsLocal(); }

export async function loadStartsAsync(): Promise<Record<string, Date>> {
  if (await isCloudMode()) {
    const c = await fetchStartsCloud();
    if (c) { writeStartsLocal(c); return c; }
  }
  return readStartsLocal();
}

export async function setStart(compoundId: string, when: Date): Promise<void> {
  const all = readStartsLocal();
  all[compoundId] = when;
  writeStartsLocal(all);
  if (await isCloudMode()) {
    const u = currentUserSync();
    if (u) {
      await supabase.from('mystack_cycle_starts').upsert({
        owner_id: u.id, compound_id: compoundId, started_at: when.toISOString(),
      }, { onConflict: 'owner_id,compound_id' });
    }
  }
}

export async function clearStart(compoundId: string): Promise<void> {
  const all = readStartsLocal();
  delete all[compoundId];
  writeStartsLocal(all);
  if (await isCloudMode()) {
    const u = currentUserSync();
    if (u) {
      await supabase.from('mystack_cycle_starts')
        .delete().eq('owner_id', u.id).eq('compound_id', compoundId);
    }
  }
}

export function loadCustom(): CustomCycle[] { return readCustomLocal(); }

export async function loadCustomAsync(): Promise<CustomCycle[]> {
  if (await isCloudMode()) {
    const c = await fetchCustomCloud();
    if (c) { writeCustomLocal(c); return c; }
  }
  return readCustomLocal();
}

export async function upsertCustom(c: CustomCycle): Promise<void> {
  const all = readCustomLocal();
  const i = all.findIndex((x) => x.id === c.id);
  if (i < 0) all.push(c); else all[i] = c;
  writeCustomLocal(all);
  if (await isCloudMode()) {
    const u = currentUserSync();
    if (u) {
      await supabase.from('mystack_custom_cycles').upsert({
        id: c.id, owner_id: u.id, label: c.label,
        on_days: c.on_days, off_days: c.off_days,
        started_at: c.started_at, notes: c.notes,
      });
    }
  }
}

export async function removeCustom(id: string): Promise<void> {
  writeCustomLocal(readCustomLocal().filter((c) => c.id !== id));
  if (await isCloudMode()) {
    const u = currentUserSync();
    if (u) {
      await supabase.from('mystack_custom_cycles')
        .delete().eq('owner_id', u.id).eq('id', id);
    }
  }
}

export function newCustomId(): string { return uuidV4(); }

export function isDisclaimerDismissed(): boolean {
  return localStorage.getItem(K_DISCLAIMER) === 'true';
}
export function dismissDisclaimer(): void {
  localStorage.setItem(K_DISCLAIMER, 'true');
}
export function resetDisclaimer(): void {
  localStorage.removeItem(K_DISCLAIMER);
}
